import React from "react";
import { toast } from "sonner";

import { useT } from "../i18n";
import safeStorage from "../lib/safeStorage";

const TOKEN_KEY = "xpnsr_seo_token";
const RETURN_KEY = "xpnsr_seo_admin_return_token";

/**
 * Sticky banner shown whenever an admin is currently impersonating
 * another user. We detect this by the presence of
 * ``xpnsr_seo_admin_return_token`` in storage — set by the
 * Admin page right before the impersonate token replaces the
 * normal admin one.
 *
 * Clicking the "Return to admin" affordance swaps the tokens back
 * and hard-navigates to the dashboard so the auth bootstrap re-runs.
 */
export default function ImpersonateBanner({ user }) {
  const t = useT();
  const returnToken = safeStorage.getItem(RETURN_KEY);
  if (!returnToken) return null;

  const handleReturn = () => {
    try {
      safeStorage.setItem(TOKEN_KEY, returnToken);
      safeStorage.removeItem(RETURN_KEY);
      toast.success(t.admin?.impersonateReturned || "Switched back to admin");
      // Hard navigation so the App-level token state + AuthContext
      // rehydrate cleanly with the admin identity.
      window.location.href = "/admin";
    } catch (e) {
      toast.error(t.admin?.impersonateReturnFailed || "Could not return to admin");
    }
  };

  const message = (t.admin?.impersonateBanner || ((email) => `Signed in as ${email} (impersonating).`))(
    user?.email || "user",
  );

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="impersonate-banner"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 60,
        background: "linear-gradient(90deg, rgba(249,115,22,0.16), rgba(249,115,22,0.04))",
        borderBottom: "1px solid rgba(249,115,22,0.4)",
        color: "var(--xp-fg, #f5f5f5)",
        padding: "8px 16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontSize: 13,
        fontFamily: "var(--xp-mono, monospace)",
      }}
    >
      <span aria-hidden="true">⚡</span>
      <span style={{ flex: 1, minWidth: 0 }}>{message}</span>
      <button
        type="button"
        onClick={handleReturn}
        data-testid="impersonate-banner-return"
        style={{
          background: "rgba(249,115,22,0.85)",
          color: "#0a0a0a",
          border: "none",
          borderRadius: 6,
          padding: "6px 12px",
          fontWeight: 600,
          fontSize: 12,
          cursor: "pointer",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {t.admin?.impersonateBannerReturn || "Return to admin"}
      </button>
    </div>
  );
}
